import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageSquare, 
  Send, 
  Paperclip, 
  FileText, 
  Download, 
  ShieldCheck, 
  Search, 
  User, 
  Clock, 
  Check, 
  CheckCheck, 
  Loader2,
  RefreshCw
} from 'lucide-react';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { showToast } from '../../components/common/Toast';
import { formatDate } from '../../utils/helpers';

export default function AdminLawyerChatPage() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [search, setSearch] = useState('');

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    setLoading(true);
    try {
      const res = await api.get('/chat/conversations');
      if (res?.success) {
        setConversations(res.conversations || []);
        if (res.conversations?.[0] && !selectedConv) {
          handleSelectConversation(res.conversations[0]);
        }
      }
    } catch (err) {
      showToast(err.message || 'Chatlarni yuklashda xatolik', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConversations();
  }, []);

  const handleSelectConversation = async (conv) => {
    setSelectedConv(conv);
    setMessagesLoading(true);
    try {
      const res = await api.get(`/chat/conversations/${conv.id}/messages`);
      if (res?.success) {
        setMessages(res.messages || []);
      }
    } catch (err) {
      showToast(err.message || 'Xabarlarni yuklashda xatolik', 'error');
    } finally {
      setMessagesLoading(false);
    }
  };

  // Polling for new messages while active
  useEffect(() => {
    if (!selectedConv?.id) return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/chat/conversations/${selectedConv.id}/messages`);
        if (res?.success) {
          setMessages(res.messages || []);
        }
      } catch (e) {}
    }, 4000);
    return () => clearInterval(interval);
  }, [selectedConv?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages.length]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputText.trim() || !selectedConv?.id || sending) return;

    const text = inputText.trim();
    setInputText('');
    setSending(true);

    try {
      const res = await api.post(`/chat/conversations/${selectedConv.id}/messages`, { text });
      if (res?.success && res?.message) {
        setMessages((prev) => [...prev, res.message]);
      }
    } catch (err) {
      showToast(err.message || 'Xabarni yuborishda xatolik', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedConv?.id) return;

    // Strict APK restriction
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    const forbidden = ['.apk', '.exe', '.bat', '.cmd', '.msi', '.vbs', '.php', '.jar', '.sh'];

    if (forbidden.includes(ext) || file.type.includes('package-archive')) {
      showToast('APK va dastur fayllarini yuklash qat’iyan taqiqlangan! Faqat rasm yoki PDF yuklang.', 'error');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (!file.type.startsWith('image/') && ext !== '.pdf' && file.type !== 'application/pdf') {
      showToast('Faqat rasm (JPG, PNG, WEBP) va hujjat (PDF) fayllari qabul qilinadi', 'error');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      showToast('Fayl hajmi 20MB dan oshmasligi kerak', 'error');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('caption', file.name);

    setUploadingFile(true);
    try {
      const res = await api.postFormData(`/chat/conversations/${selectedConv.id}/upload`, formData);
      if (res?.success && res?.message) {
        setMessages((prev) => [...prev, res.message]);
        showToast('Fayl xavfsiz formatda yuborildi!', 'success');
      }
    } catch (err) {
      showToast(err.message || 'Faylni yuborishda xatolik', 'error');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const filteredConversations = conversations.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      c.client?.fullName?.toLowerCase().includes(q) ||
      c.client?.phone?.includes(q) ||
      c.lastMessage?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="flex flex-col h-[calc(100vh-125px)] min-h-[500px] space-y-3 animate-admin-fade-in overflow-hidden">
      
      {/* Header Bar */}
      <div className="shrink-0 bg-[#090e18] border border-[#c5a880]/30 rounded-xl p-3 sm:p-3.5 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-3 text-slate-200">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl bg-[#142034] border border-[#c5a880]/40 flex items-center justify-center text-[#c5a880]">
            <MessageSquare className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-serif text-base font-bold text-slate-100">
              Mijozlar Bilan Xavfsiz Chat
            </h3>
            <span className="text-xs text-slate-400">
              Sizga murojaat qilgan fuqarolar bilan individual yozishmalar va hujjat almashinuvi
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={loadConversations}
          className="px-3 py-1.5 rounded-lg bg-[#142034] hover:bg-[#1b2b46] border border-slate-700 text-xs font-semibold text-slate-200 flex items-center transition-colors cursor-pointer"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
          Yangilash
        </button>
      </div>

      {/* Chat 2-Column Dashboard - Full Viewport Fit */}
      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4 overflow-hidden">
        
        {/* Left Column: Clients List */}
        <div className="lg:col-span-5 bg-white rounded-xl border border-slate-200 shadow-2xs flex flex-col h-full min-h-0 overflow-hidden">
          <div className="p-3 border-b border-slate-100">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Mijoz ismi yoki telefonini izlash..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-[#c5a880]"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
            {loading ? (
              <div className="py-12 text-center text-xs text-slate-400">Yuklanmoqda...</div>
            ) : filteredConversations.length === 0 ? (
              <div className="py-12 text-center text-xs text-slate-400">Hozircha mijozlar bilan chat mavjud emas</div>
            ) : (
              filteredConversations.map((c) => {
                const isSelected = selectedConv?.id === c.id;

                return (
                  <div
                    key={c.id}
                    onClick={() => handleSelectConversation(c)}
                    className={`p-3.5 transition-colors cursor-pointer ${
                      isSelected
                        ? 'bg-amber-50/70 border-l-4 border-[#c5a880]'
                        : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-xs text-slate-900 truncate">
                          {c.client?.fullName || 'Fuqaro'}
                        </div>
                        <div className="text-[11px] font-mono text-slate-500 mt-0.5">
                          {c.client?.phone}
                        </div>
                      </div>
                      {c.lawyerUnread > 0 && (
                        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-[#c5a880] text-slate-950 ml-2 shrink-0">
                          {c.lawyerUnread} yangi
                        </span>
                      )}
                    </div>

                    <p className="text-[11px] text-slate-500 truncate mt-1">
                      {c.lastMessage || 'Yozishma boshlangan'}
                    </p>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right Column: Chat Room */}
        <div className="lg:col-span-7 bg-[#070c16] rounded-xl border border-slate-800 shadow-2xs flex flex-col h-full min-h-0 overflow-hidden text-slate-200">
          {selectedConv ? (
            <>
              {/* Header */}
              <div className="p-3 sm:p-3.5 border-b border-[#162238] bg-[#090e18] flex items-center justify-between shrink-0">
                <div>
                  <h4 className="font-serif text-sm font-bold text-slate-100">
                    {selectedConv.client?.fullName}
                  </h4>
                  <span className="text-[10px] text-slate-400 block font-mono">
                    {selectedConv.client?.phone} • {selectedConv.subject || 'Yuridik maslahat'}
                  </span>
                </div>

                <div className="flex items-center text-[10px] font-semibold text-emerald-400 px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30">
                  <ShieldCheck className="w-3.5 h-3.5 mr-1" />
                  Himoyalangan kanal
                </div>
              </div>

              {/* Messages Body */}
              <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4 space-y-3 bg-[#050912]">
                {messagesLoading ? (
                  <div className="py-12 text-center text-xs text-slate-400">Xabarlar yuklanmoqda...</div>
                ) : messages.length === 0 ? (
                  <div className="py-12 text-center text-xs text-slate-400">Ushbu mijoz bilan xabarlar yo‘q</div>
                ) : (
                  messages.map((m) => {
                    const isLawyer = m.senderType === 'LAWYER';

                    return (
                      <div
                        key={m.id}
                        className={`flex flex-col ${isLawyer ? 'items-end' : 'items-start'}`}
                      >
                        <div
                          className={`max-w-[85%] rounded-2xl p-3 shadow-md ${
                            isLawyer
                              ? 'bg-[#18263e] border border-[#2a4066] text-slate-100 rounded-tr-xs'
                              : 'bg-[#0e1627] border border-[#1c2e4f] text-slate-200 rounded-tl-xs'
                          }`}
                        >
                          {!isLawyer && (
                            <span className="text-[10px] font-semibold text-[#c5a880] block mb-1">
                              Mijoz: {m.senderName}
                            </span>
                          )}

                          {/* File Attachment */}
                          {m.fileUrl && (
                            <div className="mb-2">
                              {m.fileType === 'IMAGE' ? (
                                <a href={m.fileUrl} target="_blank" rel="noopener noreferrer">
                                  <img src={m.fileUrl} alt="Hujjat" className="rounded-lg max-h-56 object-cover border border-slate-700 hover:opacity-95" />
                                </a>
                              ) : (
                                <a
                                  href={m.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center space-x-2.5 p-2.5 rounded-lg bg-[#070b14] border border-[#1e2f4c] hover:border-[#c5a880] transition-colors"
                                >
                                  <FileText className="w-5 h-5 text-red-400 shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <span className="text-xs font-semibold text-slate-200 block truncate">
                                      {m.fileName || 'PDF Hujjat'}
                                    </span>
                                    <span className="text-[10px] text-slate-400">
                                      PDF format • Yuklab olish
                                    </span>
                                  </div>
                                  <Download className="w-4 h-4 text-slate-400 shrink-0" />
                                </a>
                              )}
                            </div>
                          )}

                          {m.text && (
                            <p className="text-xs sm:text-sm leading-relaxed whitespace-pre-line break-words">
                              {m.text}
                            </p>
                          )}

                          <div className="flex items-center justify-end space-x-1.5 mt-1 pt-0.5 text-[9px] text-slate-400">
                            <span>
                              {new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {isLawyer && (
                              m.isRead ? <CheckCheck className="w-3 h-3 text-[#c5a880]" /> : <Check className="w-3 h-3 text-slate-400" />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Form */}
              <form onSubmit={handleSendMessage} className="p-3 bg-[#090e18] border-t border-[#162238] flex items-center space-x-2 shrink-0">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  disabled={uploadingFile}
                  onClick={() => fileInputRef.current?.click()}
                  title="Rasm yoki PDF biriktirish (APK taqiqlangan)"
                  className="w-9 h-9 rounded-xl bg-[#0f1728] hover:bg-[#18253d] border border-slate-700 hover:border-[#c5a880] flex items-center justify-center text-slate-400 hover:text-[#c5a880] transition-colors cursor-pointer shrink-0 disabled:opacity-50"
                >
                  {uploadingFile ? <Loader2 className="w-4 h-4 animate-spin text-[#c5a880]" /> : <Paperclip className="w-4 h-4" />}
                </button>

                <input
                  type="text"
                  placeholder="Mijozga javob yozing..."
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  className="flex-1 bg-[#050912] border border-[#162238] focus:border-[#c5a880] rounded-xl px-4 py-2 text-xs sm:text-sm text-slate-100 placeholder-slate-500 focus:outline-none transition-colors"
                />

                <button
                  type="submit"
                  disabled={!inputText.trim() || sending}
                  className="w-9 h-9 rounded-xl bg-[#c5a880] hover:bg-[#d4af37] disabled:opacity-40 text-slate-950 flex items-center justify-center transition-all cursor-pointer shrink-0 shadow-md"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-slate-500 text-center">
              <MessageSquare className="w-12 h-12 mb-2 opacity-30 text-[#c5a880]" />
              <p className="text-xs">Yozishmani boshlash uchun chap tomondan mijozni tanlang</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
